import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ApiError, api, ago, on, duration, type AuditBody, type AuditEvent } from "../api";
import i18n from "../i18n";
import { Loading } from "./Look";

/**
 * Who held this bench, who touched it, and whether those are the same people.
 *
 * The lease log always answered the first question. The second had no answer at
 * all outside one laptop, which is how two runs drove the same DUT for ninety
 * minutes on 2026-08-27 with the evidence sitting on two machines that could
 * not see each other.
 *
 * So the picture is built around the ONE thing worth seeing at a glance: an
 * access mark with no lease band under it. Occupancy is a band; accesses are
 * ticks above it, at the same time scale. A tick over a gap - or over somebody
 * else's segment - is unattended work, and it is meant to be obvious without
 * reading a single row.
 *
 * Holders are told apart by NAME, not by hue. Actors are unbounded, and a
 * categorical palette that cycles claims a distinction it cannot keep.
 */

const BAND_H = 18;      // occupancy band
const TICK_H = 12;      // access marks above it
const GAP = 2;          // the surface gap every other chart here uses
const PAD = 8;

type Segment = { from: number; to: number; actor: string; purpose: string };

/**
 * Lease events -> occupied intervals. Exported for the test.
 *
 * `openEnd` is where an unclosed lease stops. A lease that expired without ever
 * being released leaves an `acquired` as the newest event and nothing to close
 * it; running that segment to NOW draws the bench as occupied while the panel
 * above says nobody holds it. Both were true, and together they were unreadable
 * - reported from the deployed page, 2026-08-29. Pass the expiry for a lapsed
 * lease and `now` only while it is genuinely live.
 */
export function occupancy(events: AuditEvent[], now: number, openEnd = now): Segment[] {
  // Oldest first: the server sends newest first because that is what a list
  // wants, and an interval needs the opposite.
  const leases = events.filter((e) => e.kind === "lease").slice().sort((a, b) => a.ts - b.ts);
  const out: Segment[] = [];
  let open: Segment | null = null;
  for (const e of leases) {
    if (e.event === "acquired" || e.event === "taken") {
      // A takeover ENDS the previous holder's segment at the same instant. Not
      // closing it would draw two people holding one bench, which is the state
      // the lease exists to make impossible - the picture must not imply it.
      if (open) {
        open.to = e.ts;
        out.push(open);
      }
      open = { from: e.ts, to: openEnd, actor: e.actor, purpose: e.purpose };
    } else if (open) {
      open.to = e.ts;
      out.push(open);
      open = null;
    }
  }
  if (open) out.push(open);          // unclosed: runs to `openEnd`
  return out.filter((s) => s.to > s.from);
}

/** Was this access covered by a lease held by the same actor? */
export function unattended(e: AuditEvent, segs: Segment[]): boolean {
  if (e.kind !== "access") return false;
  if (e.access === "read") return false;      // reading under someone else is allowed
  return !segs.some((s) => e.ts >= s.from && e.ts <= s.to && s.actor === e.actor);
}

const MARK: Record<string, string> = {
  denied: "var(--audit-denied)",
  taken: "var(--audit-taken)",
  peek: "var(--audit-read)",
};

/**
 * A word for a value this file does not own.
 *
 * `kind` and `event` come from the service and from the harness, and the
 * harness adds outcomes without asking anybody here. The known ones get a
 * translation; anything else is shown exactly as it arrived, which is more
 * useful than a blank cell and infinitely more useful than the raw key. The
 * cast is the price of a lookup whose set is not known at compile time.
 */
function auditWord(group: "kind" | "event", value: string): string {
  if (!value) return "";
  const key = `audit.${group}.${value}` as "audit.kind.lease";
  return i18n.t(key, { defaultValue: value });
}

function markColour(e: AuditEvent): string {
  return MARK[e.event] || (e.access === "read" ? "var(--audit-read)" : "var(--audit-write)");
}

export default function AuditTimeline({ benchId }: { benchId: number }) {
  const { t } = useTranslation();
  const [body, setBody] = useState<AuditBody | null>(null);
  const [error, setError] = useState("");
  const [hover, setHover] = useState<AuditEvent | null>(null);

  useEffect(() => {
    let live = true;
    api
      .audit(benchId)
      .then((d) => live && setBody(d))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [benchId]);

  const view = useMemo(() => {
    if (!body) return null;
    const now = Date.now() / 1000;
    const events = body.events || [];
    // Where an unclosed lease stops. Held now -> it runs to now. Expired
    // without a release -> it stopped at its deadline, and drawing it to now
    // would contradict the panel above, which correctly says nobody holds the
    // bench. That contradiction is what got reported.
    const lapsed = !body.lease && body.last_lease ? body.last_lease : null;
    const openEnd = body.lease ? now : (lapsed ? lapsed.expires_at : now);
    const segs = occupancy(events, now, openEnd);
    const accesses = events.filter((e) => e.kind === "access");
    const stamps = events.map((e) => e.ts).filter(Boolean);
    // An hour of context when there is only one event, so a single mark does
    // not sit on a zero-width axis.
    const from = stamps.length ? Math.min(...stamps, ...segs.map((s) => s.from)) : now - 3600;
    const to = Math.max(now, ...stamps);
    const span = Math.max(to - from, 60);
    return { segs, accesses, from, span, now, events, lapsed };
  }, [body]);

  if (error) return <p className="mt-4 text-sm text-red-700 dark:text-red-400">{error}</p>;
  if (!body || !view)
    return <Loading label={t("audit.loading")} />;

  const x = (ts: number) => ((ts - view.from) / view.span) * 100;

  // "No access records" is NOT the same as "no history". The first version said
  // "never reported — unaudited, not idle" above a timeline full of leases and
  // runs, which reads as a broken page. Say which of the two it is.
  const noAccess = body.last_access_at == null;
  const hasHistory = view.events.length > 0;
  const loose = view.accesses.filter((e) => unattended(e, view.segs));

  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t("audit.heading")}
        </h2>

        {/* WHO HOLDS IT, in the timeline itself. The lease panel above answers
            this for the live case only, so a lease that expired without a
            release left the page saying "nobody is holding this bench" directly
            above a log whose newest line was somebody acquiring it. Both true,
            and unreadable together — reported from the deployed page. */}
        {body.lease ? (
          <span className="rounded bg-sky-50 px-1.5 py-0.5 text-xs text-sky-900 dark:bg-sky-950 dark:text-sky-200">
            <Trans
              i18nKey={
                body.lease.purpose ? "audit.heldNowByFor" : "audit.heldNowBy"
              }
              values={{
                actor: body.lease.actor,
                purpose: body.lease.purpose,
              }}
              components={{ b: <b /> }}
            />
          </span>
        ) : view.lapsed ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            <Trans
              i18nKey="audit.lapsed"
              values={{
                actor: view.lapsed.actor,
                when: ago(view.lapsed.expires_at),
              }}
              components={{ b: <b /> }}
            />
          </span>
        ) : (
          <span className="text-xs text-slate-500">{t("audit.free")}</span>
        )}

        {noAccess ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {hasHistory ? t("audit.leasesOnly") : t("audit.neverReported")}
          </span>
        ) : (
          <span className="text-xs text-slate-500">
            {t("audit.lastAccess", { when: ago(body.last_access_at) })}
          </span>
        )}
        {loose.length > 0 && (
          <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700 dark:bg-red-950 dark:text-red-200">
            {t("audit.looseWrites", { count: loose.length })}
          </span>
        )}
      </div>

      {/* Occupancy band + access ticks, one time scale.

          Positioned elements, not an SVG with preserveAspectRatio="none". That
          SVG scaled the X axis by width/100, so the 2px surface gap between
          adjacent leases became 0.2 viewBox units - about 0.7px on a phone.
          Two different holders back to back rendered as ONE unbroken block,
          which is what got reported: "same colours on the timeline for
          different leases". Percent offsets with a pixel-sized gap keep 2px at
          2px whatever the screen. */}
      <div className="relative mt-3 w-full overflow-hidden rounded"
           style={{ height: BAND_H + TICK_H + PAD }}>
        <div className="absolute rounded" style={{
          left: 0, right: 0, top: TICK_H + PAD, height: BAND_H,
          background: "var(--chart-track)",
        }} />
        {view.segs.map((s, i) => (
          <div
            key={`s${i}`}
            className="absolute rounded-sm"
            title={`${s.actor} — ${s.purpose || t("audit.noPurpose")}\n` +
                   `${on(s.from)} · ${duration(s.from, s.to)}`}
            style={{
              left: `${x(s.from)}%`,
              width: `max(2px, calc(${x(s.to) - x(s.from)}% - ${GAP}px))`,
              top: TICK_H + PAD,
              height: BAND_H,
              // Tone alternates by POSITION and encodes nothing - it is a
              // boundary cue, so two consecutive leases cannot read as one
              // block. Identity is the name in the list below and in the
              // tooltip: actors are unbounded, and a palette that cycles
              // claims a distinction it cannot keep.
              background: "var(--audit-held)",
              opacity: i % 2 ? 0.72 : 1,
            }}
          />
        ))}
        {view.accesses.map((e, i) => (
          <div
            key={`a${i}`}
            className="absolute rounded-sm"
            title={`${auditWord("event", e.event)} · ${e.actor} · ${e.tool || "?"}\n${e.purpose}`}
            onMouseEnter={() => setHover(e)}
            onMouseLeave={() => setHover(null)}
            style={{
              left: `${x(e.ts)}%`, width: 2, top: PAD, height: TICK_H,
              background: markColour(e),
            }}
          />
        ))}
      </div>

      {/* Who held it, as a list rather than as labels pinned to the band.
          Absolutely-positioned labels collided the moment two leases were close
          together in time - on the deployed page two names were painted on top
          of each other and neither was readable, which is worse than no label.
          A wrapping list cannot collide, and on a phone it is the only shape
          that fits at all. Newest first, matching the table below. */}
      <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
        {view.segs.slice(-4).reverse().map((s, i) => (
          <li key={`l${i}`} className="flex items-baseline gap-1">
            <span className="inline-block h-2 w-2 rounded-sm"
                  style={{ background: "var(--audit-held)" }} />
            <span className="max-w-[16rem] truncate" title={s.purpose}>{s.actor}</span>
            <span className="text-slate-400">{duration(s.from, s.to)}</span>
          </li>
        ))}
      </ul>

      {/* A legend is not optional above one series, and the marks carry words
          as well as colour - status by hue alone is not readable for everyone. */}
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {(
          [
            ["var(--audit-held)", "audit.legendHeld"],
            ["var(--audit-write)", "audit.legendWrite"],
            ["var(--audit-read)", "audit.legendRead"],
            ["var(--audit-taken)", "audit.legendTaken"],
            ["var(--audit-denied)", "audit.legendDenied"],
          ] as const
        ).map(([c, key]) => (
          <li key={key} className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: c }} />
            {t(key)}
          </li>
        ))}
      </ul>

      {hover && (
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
          {on(hover.ts)} · <b>{auditWord("event", hover.event)}</b> · {hover.actor}
          {hover.tool ? ` · ${hover.tool}` : ""} — {hover.purpose}
        </p>
      )}

      {/* The table view. The picture answers "was anything unattended"; this
          answers "what exactly happened", and one of the two must be readable
          without colour at all. */}
      <div className="mt-3 max-h-80 overflow-auto rounded border border-slate-200 dark:border-slate-800">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-slate-900">
            <tr>
              <th className="px-2 py-1 font-medium">{t("audit.colWhen")}</th>
              <th className="px-2 py-1 font-medium">{t("audit.colKind")}</th>
              <th className="px-2 py-1 font-medium">{t("audit.colWhat")}</th>
              <th className="px-2 py-1 font-medium">{t("audit.colWho")}</th>
              <th className="px-2 py-1 font-medium">{t("audit.colDetail")}</th>
            </tr>
          </thead>
          <tbody>
            {view.events.map((e, i) => (
              <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                <td className="whitespace-nowrap px-2 py-1 text-slate-500">{on(e.ts)}</td>
                <td className="px-2 py-1 text-slate-500">{auditWord("kind", e.kind)}</td>
                <td className="px-2 py-1 text-slate-900 dark:text-slate-100">
                  {auditWord("event", e.event)}
                  {unattended(e, view.segs) && (
                    <span className="ml-1 text-red-700 dark:text-red-400">
                      {t("audit.noLease")}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1">{e.actor || t("common.dash")}</td>
                <td className="px-2 py-1 text-slate-500">{e.purpose}</td>
              </tr>
            ))}
            {view.events.length === 0 && (
              <tr>
                <td colSpan={5} className="px-2 py-3 text-slate-500">
                  {t("audit.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
